'use client'

/**
 * Landing 示範卡的麥克風錄音 hook:getUserMedia + MediaRecorder +
 * AnalyserNode(音量驅動波形)。純邏輯、可用 mock 測試;所有瀏覽器
 * API 都在 client 端 guard,SSR 安全。錄音達 maxDurationMs 自動停止,
 * 手動 stop() 也走同一條 onStop(blob) 路徑。
 */

import { reportHandledException } from '@/lib/error-reporting'
import { useCallback, useEffect, useRef, useState } from 'react'
import { downsampleFrequencyData } from './analyser-levels'
import { DEMO_MIC_CONSTRAINTS } from './mic-capture'
import { raceWithTimeout } from './race-with-timeout'

/** stop() 發起後 onstop 收尾完成前的 wind-down window,start() 撞上時等它的上限。 */
const WIND_DOWN_TIMEOUT_MS = 2_000

export type MicUnavailableReason = 'unsupported' | 'permission_denied' | 'no_device'

export type MicStartResult = { ok: true } | { ok: false; reason: MicUnavailableReason }

/** getUserMedia rejections that mean "no usable microphone", not "permission refused". */
const NO_DEVICE_ERRORS = new Set(['NotFoundError', 'OverconstrainedError'])

/** Why a `getUserMedia` rejection means the microphone cannot be used. */
export function micStartFailureReason(error: unknown): 'permission_denied' | 'no_device' {
  const name = error instanceof Error || error instanceof DOMException ? error.name : ''
  return NO_DEVICE_ERRORS.has(name) ? 'no_device' : 'permission_denied'
}

export interface UseMicRecorderOptions {
  /** 錄音上限,逾時自動停止(預設 15 秒)。 */
  maxDurationMs?: number
  /** 錄音結束(手動或自動)時收到完整音檔。 */
  onStop: (blob: Blob) => void
}

export interface MicRecorderControls {
  recording: boolean
  isSupported: () => boolean
  /**
   * Start recording. Pass a shared `MediaStream` to reuse a single mic capture
   * (the demo card opens ONE `getUserMedia` and feeds the same stream to both the
   * recorder and the PCM worklet — opening two independent captures for the same
   * physical mic races on real hardware and corrupts one of them). When a shared
   * stream is passed this hook does NOT stop its tracks on teardown; the caller
   * that opened it owns its lifecycle.
   */
  start: (externalStream?: MediaStream) => Promise<MicStartResult>
  stop: () => void
  /** 把目前頻譜降採樣成 count 個 0..1 音量值(無 analyser 時全 0)。 */
  getLevels: (count: number) => number[]
}

const DEFAULT_MAX_DURATION_MS = 15_000

/** mimeType 優先序:webm/opus(Chrome/Firefox)→ mp4(Safari)。 */
const PREFERRED_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return undefined
  }
  return PREFERRED_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type))
}

interface ActiveSession {
  recorder: MediaRecorder
  stream: MediaStream
  /** True when this hook opened the stream (so it must stop the tracks). False
   *  for a caller-owned shared stream — stopping it would kill the other consumer. */
  ownsStream: boolean
  audioContext: AudioContext | null
  analyser: AnalyserNode | null
  freqData: Uint8Array | null
  autoStopTimer: ReturnType<typeof setTimeout>
}

export function useMicRecorder(options: UseMicRecorderOptions): MicRecorderControls {
  const maxDurationMs = options.maxDurationMs ?? DEFAULT_MAX_DURATION_MS
  const onStopRef = useRef(options.onStop)
  onStopRef.current = options.onStop

  const sessionRef = useRef<ActiveSession | null>(null)
  // stop() 發起 → onstop 收尾完成 的 wind-down promise。撞上收尾中 session 的 start() 會等它,
  // 免拿到還在等 onstop 的舊 session(sessionRef 未清、chunks 未重置)而靜默沒錄到(RC1)。
  const pendingStopRef = useRef<{ promise: Promise<void>; resolve: () => void } | null>(null)
  // 元件是否已卸載:start() 在 wind-down / getUserMedia 等 await 之後檢查,已卸載就收掉手上
  // 資源、不建新 MediaRecorder / 不 setRecording(否則 own-stream 情境會洩麥克風,M1)。
  const unmountedRef = useRef(false)
  const [recording, setRecording] = useState(false)

  const isSupported = useCallback(() => {
    return (
      typeof navigator !== 'undefined' &&
      typeof navigator.mediaDevices?.getUserMedia === 'function' &&
      typeof MediaRecorder !== 'undefined'
    )
  }, [])

  const releaseSession = useCallback(() => {
    const session = sessionRef.current
    if (!session) return
    sessionRef.current = null
    clearTimeout(session.autoStopTimer)
    // Only stop the tracks we opened. A shared (caller-owned) stream is stopped
    // by whoever opened it — stopping it here would cut off the PCM worklet too.
    if (session.ownsStream) {
      for (const track of session.stream.getTracks()) track.stop()
    }
    if (session.audioContext && session.audioContext.state !== 'closed') {
      session.audioContext.close().catch((error: unknown) => {
        reportHandledException(error, { source: 'lib/use-mic-recorder.ts' })
        // 視覺用資源關閉失敗不影響流程 — 記錄後繼續
        console.warn('[landing-demo] failed to close AudioContext', error)
      })
    }
  }, [])

  /** 發起停止:建立 wind-down promise(onstop 收尾完成時 resolve)後才 recorder.stop()。 */
  const beginStop = useCallback((recorder: MediaRecorder) => {
    if (recorder.state === 'inactive') return
    if (!pendingStopRef.current) {
      let resolve!: () => void
      const promise = new Promise<void>((r) => {
        resolve = r
      })
      pendingStopRef.current = { promise, resolve }
    }
    recorder.stop()
  }, [])

  const stop = useCallback(() => {
    const session = sessionRef.current
    if (session) beginStop(session.recorder)
  }, [beginStop])

  const start = useCallback(
    async (externalStream?: MediaStream): Promise<MicStartResult> => {
    if (!isSupported()) return { ok: false, reason: 'unsupported' }
    if (sessionRef.current) {
      // 仍在錄音 → 維持現行 early-return(呼叫端已在錄,忽略重複 start)。
      if (sessionRef.current.recorder.state !== 'inactive') return { ok: true }
      // 收尾中(stop() 已發起、onstop 尚未 fire):等 wind-down 完成(onstop → releaseSession
      // 清 sessionRef + resolve pendingStop),免撞舊 session、拿到沒重置的 chunks(RC1)。
      const pending = pendingStopRef.current
      if (pending) await raceWithTimeout(pending.promise, WIND_DOWN_TIMEOUT_MS, null)
      // M1:wind-down 等待中卸載 → 別開新 recorder / 不 setRecording(尚未取得資源,直接退)。
      if (unmountedRef.current) return { ok: false, reason: 'unsupported' }
      // 等完仍卡住(onstop 逾時沒 fire)→ 視同啟動失敗,呼叫端走 realtime-only + take cap 兜底。
      if (sessionRef.current) return { ok: false, reason: 'unsupported' }
    }

    // A caller-supplied shared stream skips getUserMedia entirely (single capture,
    // no race with the PCM worklet). Otherwise open our own (owned → we stop it).
    let stream: MediaStream
    const ownsStream = externalStream === undefined
    if (externalStream) {
      stream = externalStream
    } else {
      try {
        // 同 demo card 的共享擷取:關 EC/NS/AGC(見 DEMO_MIC_CONSTRAINTS),避免 standalone
        // fallback 拿到處理過的音訊(正是會劣化 STT 的來源)。
        stream = (await navigator.mediaDevices.getUserMedia(DEMO_MIC_CONSTRAINTS)) as MediaStream
      } catch (error) {
          reportHandledException(error, { source: 'lib/use-mic-recorder.ts' })
        // 使用者拒絕或裝置不可用 — 呼叫端會退回腳本示範(可見的 recovery)
          console.warn(
            '[landing-demo] microphone unavailable — falling back to scripted demo',
            error
          )
        return { ok: false, reason: micStartFailureReason(error) }
      }
    }
    // M1:getUserMedia await 後卸載 → 停自有 stream、別開 recorder(否則 own-stream 洩麥克風)。
    if (unmountedRef.current) {
      if (ownsStream) for (const track of stream.getTracks()) track.stop()
      return { ok: false, reason: 'unsupported' }
    }

    // analyser 只供波形視覺;建立失敗不影響錄音本體
    let audioContext: AudioContext | null = null
    let analyser: AnalyserNode | null = null
    let freqData: Uint8Array | null = null
    try {
      const AudioContextCtor =
        typeof AudioContext !== 'undefined'
          ? AudioContext
          : (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (AudioContextCtor) {
        audioContext = new AudioContextCtor()
        analyser = audioContext.createAnalyser()
        analyser.fftSize = 256
        audioContext.createMediaStreamSource(stream).connect(analyser)
        freqData = new Uint8Array(analyser.frequencyBinCount)
      }
    } catch (error) {
        reportHandledException(error, { source: 'lib/use-mic-recorder.ts' })
      // 波形退化成靜態(CSS fallback),錄音照常
      console.warn('[landing-demo] audio analyser unavailable — wave falls back to static', error)
      audioContext = null
      analyser = null
      freqData = null
    }

    const mimeType = pickMimeType()
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
    const chunks: Blob[] = []
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunks.push(event.data)
    }
    recorder.onstop = () => {
      releaseSession()
      setRecording(false)
      // 先 resolve wind-down(sessionRef 已於 releaseSession 清空)→ 等待中的 start() 開新 session;
      // 再回呼 onStop(blob)。
      const pending = pendingStopRef.current
      pendingStopRef.current = null
      pending?.resolve()
      onStopRef.current(new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' }))
    }

    const autoStopTimer = setTimeout(() => {
      beginStop(recorder) // 15s 自動停也走 wind-down(建 pendingStop 後才 stop)
    }, maxDurationMs)

      sessionRef.current = {
        recorder,
        stream,
        ownsStream,
        audioContext,
        analyser,
        freqData,
        autoStopTimer,
      }
    recorder.start()
    setRecording(true)
    return { ok: true }
    },
    [isSupported, maxDurationMs, releaseSession, beginStop]
  )

  const getLevels = useCallback((count: number): number[] => {
    const session = sessionRef.current
    if (!session?.analyser || !session.freqData) return new Array<number>(count).fill(0)
    return downsampleFrequencyData(session.analyser, session.freqData, count)
  }, [])

  // unmount:終止錄音並釋放資源,但不再回呼 onStop
  useEffect(() => {
    // remount-safe(React StrictMode dev 雙掛載):setup 時重置,免得前一輪 cleanup 的
    // true 卡住同一個 ref、讓 start() 永遠拒絕。
    unmountedRef.current = false
    return () => {
      unmountedRef.current = true
      // 解鎖任何在 wind-down 等待的 start()(下方會 null 掉 onstop,不會自行 resolve pendingStop);
      // 該 start() 醒來後讀 unmountedRef → 直接退,不建新 recorder。
      pendingStopRef.current?.resolve()
      pendingStopRef.current = null
      const session = sessionRef.current
      if (!session) return
      session.recorder.onstop = null
      if (session.recorder.state !== 'inactive') session.recorder.stop()
      releaseSession()
    }
  }, [releaseSession])

  return { recording, isSupported, start, stop, getLevels }
}
